import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatTargetMetricValue,
  muscleKey,
  type BodyFigure,
  type CatalogMuscleSlug,
  type UnitsPreference,
} from "@jackedlogs/shared";

import { BodyMap } from "../bodymap/BodyMap";
import { MiniTrendBars } from "../primitives/MiniTrendBars";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { TargetMeterRow } from "../primitives/TargetMeterRow";
import { zoneFillColor, zoneWord } from "../primitives/TargetBar";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";
import {
  muscleZoneBodyMapValue,
  type MuscleCoverage,
} from "./muscleCoverage";

// PG-1 — the Progress "muscle coverage" hero. The one surface that answers "am I
// hitting my weekly muscle targets", built entirely from existing primitives:
// the dual bodymap figure (zone-coloured), a ScaleLegend key BELOW it (DS-16b),
// and one <TargetMeterRow> per muscle (label + actual/target + zone glyph+word +
// <TargetBar>) with a <MiniTrendBars> 4-week reference beside its average. The
// attention rows (under / no-coverage) show first; the rest fold behind "see
// all". Fixed weekly cadence — the Progress period selector drives the trend
// sections, not this (targets are weekly by nature).

// Card-width floor for the two-column (figure | list) layout. Below it the
// list column can't hold a TargetMeterRow label + bar + trend without crushing,
// so we stay stacked. Figure column ≈250px + gap 16 + card padding 32 + a ~340px
// list floor ≈ 640.
const COVERAGE_TWO_COL_MIN = 640;

export type MuscleCoverageSectionProps = {
  theme: UiTheme;
  /** Null when the read has not resolved, failed, or scored nothing — which
   *  are three DIFFERENT answers, told apart by `loading` / `error` below. */
  coverage: MuscleCoverage | null;
  units: UnitsPreference;
  locale: string;
  figure?: BodyFigure;
  isDark?: boolean;
  /** The session-results read has not come back yet. */
  loading?: boolean;
  /** The session-results read failed. */
  error?: unknown;
};

export function MuscleCoverageSection({
  theme,
  coverage,
  units,
  locale,
  figure,
  isDark = false,
  loading = false,
  error,
}: MuscleCoverageSectionProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { expanded, toggle, a11yProps } = useDisclosure(false);
  // Two-column (figure beside the list) only when the CARD itself is wide
  // enough, measured via onLayout — NOT the raw window width. The desktop
  // web-rnw shell spends ~288px on its rail + padding before this card sees
  // the viewport, so a window-width breakpoint (768) would flip to 2-col while
  // the card is still ~480px and crush the muscle rows (a11y regression, worst
  // in long-string locales). Measuring the real width also lets a genuinely
  // wide native tablet get the 2-col without a Platform branch.
  const [cardWidth, setCardWidth] = useState(0);
  const twoCol = cardWidth >= COVERAGE_TWO_COL_MIN;
  const onCardLayout = (e: LayoutChangeEvent) =>
    setCardWidth(e.nativeEvent.layout.width);

  const bodyValue = useMemo(
    () => muscleZoneBodyMapValue((coverage?.rows ?? []).map((r) => r.row), theme),
    [coverage, theme],
  );

  // Collapsed = the attention rows (under / no-coverage); when everything's on
  // target, still show a few so the section isn't a lone sentence.
  const rows = coverage?.rows ?? [];
  const collapsedCount =
    coverage && coverage.attentionCount > 0
      ? coverage.attentionCount
      : Math.min(3, rows.length);
  const shown = expanded ? rows : rows.slice(0, collapsedCount);
  const hiddenCount = rows.length - collapsedCount;

  const legendItems = [
    { color: zoneFillColor(theme, "under"), label: zoneWord(t, "under") },
    { color: zoneFillColor(theme, "in-band"), label: zoneWord(t, "in-band") },
    { color: zoneFillColor(theme, "over"), label: zoneWord(t, "over") },
  ];

  const title = t("progress.coverageTitle", { defaultValue: "Muscle coverage" });
  const subtitle = t("progress.coverageSubtitle", {
    defaultValue: "vs weekly target",
  });

  // ⛔ ONE eyebrow for all four states below. It carries the heading role and
  // the joined label, because this section is now the FIRST thing on both
  // Progress and the coach's athlete detail: before PG-8 the screen opened on
  // a titled card and a screen reader had a landmark to land on. Removing that
  // card without giving this one a heading would have left the surface with no
  // heading at all until the period selector, which announces as a control.
  const eyebrow = (
    <View
      style={styles.eyebrow}
      accessibilityRole="header"
      accessibilityLabel={`${title} — ${subtitle}`}
    >
      <Text style={styles.eyebrowLbl}>{title}</Text>
      <Text style={styles.eyebrowAux}>{subtitle}</Text>
    </View>
  );

  // The three answers that are NOT "here is your coverage", kept apart on
  // purpose. Collapsing them is how a broken read and an empty history come to
  // look identical, which is exactly what the deleted panel documented and
  // what deleting it would otherwise have re-introduced.
  if (error) {
    return (
      <View style={styles.wrap}>
        {eyebrow}
        <View style={[styles.card, styles.cardError]}>
          <Text style={styles.stateText}>
            {t("mobile.progress.loadFailed", { defaultValue: "Couldn’t load" })}
          </Text>
        </View>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.wrap}>
        {eyebrow}
        <View style={styles.card}>
          <ActivityIndicator color={theme.palette.brand500} />
        </View>
      </View>
    );
  }

  if (!coverage) {
    return (
      <View style={styles.wrap}>
        {eyebrow}
        <View style={styles.card}>
          <Text style={styles.stateText}>
            {t("progress.coverageNoData", {
              defaultValue:
                "Nothing to score yet — log a session, or set a weekly target.",
            })}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      {eyebrow}

      <View
        style={[styles.card, twoCol ? styles.cardWide : null]}
        onLayout={onCardLayout}
      >
        {Object.keys(bodyValue).length > 0 ? (
          <View
            style={[styles.figureBlock, twoCol ? styles.figureBlockWide : null]}
          >
            <BodyMap
              mode="display"
              view="front"
              value={bodyValue}
              theme={theme}
              isDark={isDark}
              scale={1.1}
              figure={figure}
              layout="dual"
            />
            <ScaleLegend variant="dots" theme={theme} items={legendItems} />
          </View>
        ) : null}

        <View style={[styles.body, twoCol ? styles.bodyWide : null]}>
        <Text style={styles.lead}>
          {coverage.mode === "degraded"
            ? t("progress.coverageTopTrained", {
                defaultValue: "Your most-trained muscles this week.",
              })
            : coverage.attentionCount > 0
              ? t("progress.coverageLead", {
                  count: coverage.attentionCount,
                  defaultValue: "Under your weekly target: {{count}}",
                })
              : t("progress.coverageAllGood", {
                  defaultValue: "Every targeted muscle is on track this week.",
                })}
        </Text>

        <View style={styles.list}>
          {shown.map(({ row, stats }, idx) => (
            <View
              key={`${row.axis}:${row.key}`}
              style={[styles.rowWrap, idx > 0 ? styles.rowDivider : null]}
            >
              <TargetMeterRow
                theme={theme}
                row={row}
                label={t(muscleKey(row.key as CatalogMuscleSlug))}
                units={units}
                locale={locale}
                degradedMax={coverage.degradedMax}
              />
              {stats.hasData ? (
                <View
                  style={styles.trendRow}
                  accessibilityElementsHidden
                  importantForAccessibility="no-hide-descendants"
                >
                  <MiniTrendBars
                    theme={theme}
                    weeks={stats.weeks}
                    height={16}
                    lastColor={
                      row.zone ? zoneFillColor(theme, row.zone) : undefined
                    }
                  />
                  <Text style={styles.avg}>
                    {t("progress.coverageAvg", {
                      n: formatTargetMetricValue(
                        stats.avgPerWeek,
                        row.metric,
                        units,
                        locale,
                      ),
                      defaultValue: "avg {{n}}/wk",
                    })}
                  </Text>
                </View>
              ) : null}
            </View>
          ))}
        </View>

        {hiddenCount > 0 ? (
          <Pressable
            onPress={toggle}
            {...a11yProps}
            hitSlop={8}
            accessibilityRole="button"
            style={styles.seeAll}
          >
            <Text style={styles.seeAllTxt}>
              {expanded
                ? t("progress.coverageShowFewer", { defaultValue: "Show fewer" })
                : t("progress.coverageSeeAll", {
                    count: hiddenCount,
                    defaultValue: "See all muscles (+{{count}})",
                  })}
            </Text>
          </Pressable>
        ) : null}
        </View>
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2 },
    cardError: { borderColor: p.danger },
    stateText: { color: p.ink3, fontSize: ty.sizes.sm },
    eyebrow: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.s3,
      paddingHorizontal: s.s1,
    },
    eyebrowLbl: {
      // ink3 (not ink2): ink2-on-bg is 4.37:1 on Catppuccin Latte, under the AA
      // floor for a reduced-vision reader. sm (not xs): the DS-16 12pt legend/
      // label floor the ScaleLegend below already enforces.
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    eyebrowAux: { color: p.ink3, fontSize: ty.sizes.sm },
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    figureBlock: { alignItems: "center", gap: s.s2 },
    // Wide: card becomes two columns (figure | list). The card's own gap turns
    // into the horizontal gutter; the figure keeps its intrinsic width and
    // pins to the top, the body column flexes to fill the rest.
    cardWide: { flexDirection: "row", alignItems: "flex-start", gap: s.s5 },
    figureBlockWide: { flexShrink: 0 },
    body: { gap: s.s3 },
    // minWidth floor is defence-in-depth below the COVERAGE_TWO_COL_MIN gate:
    // even if the card is measured a hair above the threshold, the rows never
    // compress under a legible width.
    bodyWide: { flex: 1, minWidth: 320 },
    lead: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "600",
      lineHeight: ty.sizes.base * 1.35,
    },
    list: { marginTop: s.s1 },
    rowWrap: { paddingVertical: s.s3 },
    rowDivider: { borderTopWidth: 1, borderTopColor: p.border },
    trendRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      marginTop: s.s2,
    },
    avg: {
      // ink3 (not ink2): ink2-on-surface is 4.06:1 on Catppuccin Latte, under
      // the AA floor. sm (not xs) per the DS-16 reduced-vision text floor.
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    seeAll: { paddingVertical: s.s2, minHeight: 44, justifyContent: "center" },
    seeAllTxt: {
      color: p.brandText,
      fontSize: ty.sizes.base,
      fontWeight: "600",
    },
  });
}
